import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { brandColors, APP_FONT_FAMILY, TYPE } from '../../theme/appTheme';
import { brandCss } from '../../theme/brandCss';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  /** Soft pastel card background (preset-tinted) */
  bg?: string;
  trend?: number;
  /** e.g. "vs last 30 days" */
  trendLabel?: string;
  subtitle?: string;
  compact?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({
  title, value, icon, color = brandCss.primary, bg, trend, trendLabel, subtitle, compact,
}) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: '14px',
      border: `1px solid ${bg ? `${color}22` : brandColors.border}`,
      bgcolor: bg || '#fff',
      boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)',
      transition: 'all 0.22s ease',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)' },
    }}
  >
    <CardContent sx={{ p: compact ? 1.75 : 2.25, '&:last-child': { pb: compact ? 1.75 : 2.25 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              mb: 0.75,
              color: brandColors.textMuted,
              fontSize: TYPE.secondary.fontSize,
              fontWeight: 500,
              fontFamily: APP_FONT_FAMILY,
              lineHeight: TYPE.secondary.lineHeight,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: brandColors.textDark,
              mb: 0.5,
              fontSize: compact ? 22 : 26,
              fontWeight: 700,
              lineHeight: 1.15,
              fontFamily: APP_FONT_FAMILY,
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">{subtitle}</Typography>
          )}
          {trend !== undefined && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                mt: 1,
              }}
            >
              {trend >= 0
                ? <TrendingUpIcon sx={{ fontSize: 14, color: color }} />
                : <TrendingDownIcon sx={{ fontSize: 14, color: brandColors.danger }} />}
              <Typography
                sx={{
                  fontSize: TYPE.helper.fontSize,
                  fontWeight: 600,
                  fontFamily: APP_FONT_FAMILY,
                  color: trend >= 0 ? color : brandColors.danger,
                  lineHeight: TYPE.helper.lineHeight,
                }}
              >
                {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
                {trendLabel ? ` ${trendLabel}` : ''}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#fff',
            color,
            width: compact ? 44 : 48,
            height: compact ? 44 : 48,
            borderRadius: '12px',
            boxShadow: `0 4px 12px ${color}28`,
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: compact ? 22 : 24 },
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);
